import {
  isBackgroundBlurEnabled,
  PreferenceKey,
  usePreferencesStore,
} from '@/entities/preference'

import { isBackgroundBlurSupported } from './create-background-blur-pipeline'
import {
  detachBackgroundBlurProcessor,
  isBackgroundBlurProcessedStream,
  maybeApplyBackgroundBlur,
} from './maybe-apply-background-blur'

const inflightByCameraStreamId = new Map<string, Promise<MediaStream>>()

/**
 * Apply background blur to a live camera stream (clone + keep original for restore).
 * Deduplicates concurrent calls for the same camera stream (camera-on upgrade + track watch).
 */
export const applyBackgroundBlurForLocalPublish = async (
  cameraStream: MediaStream,
): Promise<MediaStream> => {
  if (isBackgroundBlurProcessedStream(cameraStream)) {
    return cameraStream
  }

  if (!isBackgroundBlurSupported() || !cameraStream.getVideoTracks().length) {
    return cameraStream
  }

  const preferencesStore = usePreferencesStore()
  if (!isBackgroundBlurEnabled(preferencesStore.getPreference(PreferenceKey.BACKGROUND_BLUR_ENABLED))) {
    return cameraStream
  }

  const sourceTrack = cameraStream.getVideoTracks()[0]
  if (!sourceTrack || sourceTrack.readyState !== 'live') {
    return cameraStream
  }

  const existing = inflightByCameraStreamId.get(cameraStream.id)
  if (existing) {
    return existing
  }

  const task = (async () => {
    const clone = sourceTrack.clone()
    const cloneStream = new MediaStream([clone])

    try {
      const processed = await maybeApplyBackgroundBlur(cloneStream, {
        restoreStream: cameraStream,
      })

      if (processed.id === cloneStream.id) {
        clone.stop()
        return cameraStream
      }

      // Preference may flip off while MediaPipe/worker starts — do not return a blur
      // stream the user already disabled (group-call toggle race).
      if (!isBackgroundBlurEnabled(preferencesStore.getPreference(PreferenceKey.BACKGROUND_BLUR_ENABLED))) {
        detachBackgroundBlurProcessor(processed)
        return cameraStream
      }

      return processed
    } catch (error) {
      clone.stop()
      console.warn('[background-blur] apply for publish failed, using raw camera', error)
      return cameraStream
    }
  })()

  inflightByCameraStreamId.set(cameraStream.id, task)
  try {
    return await task
  } finally {
    inflightByCameraStreamId.delete(cameraStream.id)
  }
}
